import R from "react"
import * as P from "../program-state"
import * as C from "../controls"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheck, faSpinner, faStar, faArrowUp, faXmark } from "@fortawesome/free-solid-svg-icons"

/******************************************************************************
 ** Usage Progress Bar Component [
 ******************************************************************************/

type usage_progress_bar_props = {
    label : string
    current : number
    limit : number | null
    show_unlimited?: boolean
}

export const Usage_progress_bar = ({label, current, limit, show_unlimited = true} : usage_progress_bar_props) => {
    if (limit === null) {
        return (
            <div>
                <div className="tw-flex tw-items-center tw-justify-between tw-text-sm tw-mb-1">
                    <span className="tw-font-medium tw-text-gray-700">{label}</span>
                    {show_unlimited && <span className="tw-text-green-600 tw-font-medium">Unlimited ✓</span>}
                </div>
            </div>
        )
    }

    const percent = Math.min((current / limit) * 100, 100)
    const is_at_limit = current >= limit
    const is_near_limit = percent >= 80 && !is_at_limit

    let bar_color = "tw-bg-big-stone-rlydark"
    if (is_at_limit) bar_color = "tw-bg-red-500"
    else if (is_near_limit) bar_color = "tw-bg-yellow-500"

    return (
        <div>
            <div className="tw-flex tw-items-center tw-justify-between tw-text-sm tw-mb-1">
                <span className="tw-font-medium tw-text-gray-700">{label}</span>
                <span className={`tw-font-medium ${is_at_limit ? 'tw-text-red-600' : is_near_limit ? 'tw-text-yellow-600' : 'tw-text-gray-600'}`}>
                    {current} / {limit}
                </span>
            </div>
            <div className="tw-w-full tw-bg-gray-200 tw-rounded-full tw-h-2">
                <div
                    className={`tw-h-2 tw-rounded-full tw-transition-all ${bar_color}`}
                    style={{ width: `${percent}%` }}
                />
            </div>
        </div>
    )
}

/******************************************************************************
 ** Usage Progress Bar Component ]
 ******************************************************************************/

/******************************************************************************
 ** Upgrade Prompt Banner Component [
 ******************************************************************************/

type upgrade_prompt_banner_props = {
    message : string
    tier_suggestion? : string
    on_upgrade : () => void
    on_dismiss? : () => void
}

export const Upgrade_prompt_banner = ({message, tier_suggestion, on_upgrade, on_dismiss} : upgrade_prompt_banner_props) => {
    return (
        <div className="tw-bg-yellow-50 tw-border tw-border-yellow-200 tw-rounded-lg tw-p-4 tw-relative">
            {on_dismiss && (
                <button
                    onClick={on_dismiss}
                    className="tw-absolute tw-top-3 tw-right-3 tw-text-yellow-600 hover:tw-text-yellow-800"
                >
                    <FontAwesomeIcon icon={faXmark} />
                </button>
            )}
            <div className="tw-flex tw-items-start tw-gap-3">
                <FontAwesomeIcon icon={faArrowUp} className="tw-text-yellow-600 tw-mt-1" />
                <div className="tw-flex-1">
                    <p className="tw-text-sm tw-text-yellow-800 tw-font-medium">
                        {message}
                    </p>
                    {tier_suggestion && (
                        <p className="tw-text-sm tw-text-yellow-700 tw-mt-1">
                            Upgrade to {tier_suggestion} for more capacity.
                        </p>
                    )}
                    <C.Eres_button
                        className="tw-mt-3"
                        onClick={on_upgrade}
                    >
                        View Upgrade Options
                    </C.Eres_button>
                </div>
            </div>
        </div>
    )
}

/******************************************************************************
 ** Upgrade Prompt Banner Component ]
 ******************************************************************************/

/******************************************************************************
 ** Plan Card Component [
 ******************************************************************************/

type plan_card_props = {
    plan : P.usage_tier_t
    is_current : boolean
    is_popular : boolean
    on_select : (plan : P.usage_tier_t) => void
    subscribing : boolean
}

const Plan_card_component = ({plan, is_current, is_popular, on_select, subscribing} : plan_card_props) => {
    const campaign_text = plan.campaign_limit
        ? `Up to ${plan.campaign_limit} campaigns`
        : 'Unlimited campaigns'

    const brand_text = plan.brand_limit
        ? `${plan.brand_limit} brand${plan.brand_limit > 1 ? 's' : ''}`
        : 'Unlimited brands'

    const team_text = plan.team_seats === 1
        ? '1 user'
        : `Up to ${plan.team_seats} users`

    return (
        <C.Eres_card className={`tw-relative tw-h-full ${is_popular || is_current ? 'tw-border-2 tw-border-big-stone-rlydark' : ''}`}>
            {is_popular && !is_current && (
                <div className="tw-absolute tw-top-3 tw-right-3 tw-bg-big-stone-rlydark tw-text-white tw-px-3 tw-py-1 tw-rounded tw-text-xs tw-font-semibold tw-flex tw-items-center tw-gap-1">
                    <FontAwesomeIcon icon={faStar} className="tw-text-xs" />
                    Most Popular
                </div>
            )}
            {is_current && (
                <div className="tw-absolute tw-top-3 tw-right-3 tw-bg-green-600 tw-text-white tw-px-3 tw-py-1 tw-rounded tw-text-xs tw-font-semibold">
                    Current Plan
                </div>
            )}

            <div className="tw-p-6">
                <div className="tw-mb-6">
                    <h3 className="tw-text-xl tw-font-bold tw-text-big-stone-900">{plan.name.replace(' (Annual)', '')}</h3>
                    <div className="tw-mt-2">
                        <span className="tw-text-3xl tw-font-bold tw-text-big-stone-rlydark">${plan.price}</span>
                        <span className="tw-text-gray-600 tw-text-sm">
                            {plan.eligibility_duration === 12 ? '/year' : '/month'}
                        </span>
                    </div>
                    {plan.eligibility_duration === 12 && (
                        <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
                            (${(plan.price / 12).toFixed(2)}/month billed annually)
                        </p>
                    )}
                    <p className="tw-text-xs tw-text-gray-600 tw-mt-2">{plan.description}</p>
                </div>

                <div className="tw-space-y-2 tw-mb-6 tw-text-sm">
                    <div className="tw-flex tw-items-start tw-gap-2">
                        <FontAwesomeIcon icon={faCheck} className="tw-text-green-600 tw-mt-0.5 tw-flex-shrink-0" />
                        <span>{brand_text}</span>
                    </div>
                    <div className="tw-flex tw-items-start tw-gap-2">
                        <FontAwesomeIcon icon={faCheck} className="tw-text-green-600 tw-mt-0.5 tw-flex-shrink-0" />
                        <span>{campaign_text}</span>
                    </div>
                    <div className="tw-flex tw-items-start tw-gap-2">
                        <FontAwesomeIcon icon={faCheck} className="tw-text-green-600 tw-mt-0.5 tw-flex-shrink-0" />
                        <span>{team_text}</span>
                    </div>
                    <div className="tw-flex tw-items-start tw-gap-2">
                        <FontAwesomeIcon icon={faCheck} className="tw-text-green-600 tw-mt-0.5 tw-flex-shrink-0" />
                        <span>All social networks</span>
                    </div>
                    {plan.features?.white_label && (
                        <div className="tw-flex tw-items-start tw-gap-2">
                            <FontAwesomeIcon icon={faCheck} className="tw-text-green-600 tw-mt-0.5 tw-flex-shrink-0" />
                            <span>White-label reports</span>
                        </div>
                    )}
                    {plan.features?.automation && (
                        <div className="tw-flex tw-items-start tw-gap-2">
                            <FontAwesomeIcon icon={faCheck} className="tw-text-green-600 tw-mt-0.5 tw-flex-shrink-0" />
                            <span>Automated rules</span>
                        </div>
                    )}
                    {plan.features?.bulk_operations && (
                        <div className="tw-flex tw-items-start tw-gap-2">
                            <FontAwesomeIcon icon={faCheck} className="tw-text-green-600 tw-mt-0.5 tw-flex-shrink-0" />
                            <span>Bulk operations</span>
                        </div>
                    )}
                </div>

                <C.Eres_button
                    className="tw-w-full"
                    onClick={() => on_select(plan)}
                    disabled={subscribing || is_current}
                >
                    {subscribing ? (
                        <>
                            <FontAwesomeIcon icon={faSpinner} spin className="tw-mr-2" />
                            Processing...
                        </>
                    ) : is_current ? (
                        'Current Plan'
                    ) : (
                        'Select Plan'
                    )}
                </C.Eres_button>
            </div>
        </C.Eres_card>
    )
}

/******************************************************************************
 ** Plan Card Component ]
 ******************************************************************************/

/******************************************************************************
 ** Plan Comparison Inline Component [
 ******************************************************************************/

type plan_comparison_inline_props = {
    plans : P.usage_tier_t[]
    current_plan_id? : number
    on_select : (plan : P.usage_tier_t) => Promise<void>
}

export const Plan_comparison_inline = ({plans, current_plan_id, on_select} : plan_comparison_inline_props) => {
    const [billing_period, set_billing_period] = R.useState<'monthly' | 'annual'>('monthly')
    const [subscribing, set_subscribing] = R.useState(false)

    const filtered_plans = plans.filter(p => {
        if (billing_period === 'monthly') {
            return p.eligibility_duration === 1
        } else {
            return p.eligibility_duration === 12
        }
    })

    const tier_names = ['Starter', 'Growth', 'Professional']
    const display_plans = tier_names
        .map(name => filtered_plans.find(p => p.name.includes(name)))
        .filter((p): p is P.usage_tier_t => p !== undefined)

    const handle_select = async (plan : P.usage_tier_t) => {
        set_subscribing(true)
        try {
            await on_select(plan)
        } finally {
            set_subscribing(false)
        }
    }

    // calculate savings for annual
    const show_savings = billing_period === 'annual' && display_plans.length > 0
    const savings_text = show_savings ? "Save 2 months!" : null

    return (
        <div className="tw-space-y-4">
            {/* billing period toggle */}
            <div className="tw-flex tw-justify-center">
                <div className="tw-relative tw-bg-gray-200 tw-p-1 tw-rounded-lg tw-flex tw-gap-1">
                    <button
                        onClick={() => set_billing_period('monthly')}
                        className={`
                            tw-px-6 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-transition-all
                            ${billing_period === 'monthly'
                                ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm'
                                : 'tw-text-gray-700 hover:tw-text-gray-900'
                            }
                        `}
                    >
                        Monthly
                    </button>
                    <button
                        onClick={() => set_billing_period('annual')}
                        className={`
                            tw-px-6 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-transition-all
                            ${billing_period === 'annual'
                                ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm'
                                : 'tw-text-gray-700 hover:tw-text-gray-900'
                            }
                        `}
                    >
                        Annual
                        {savings_text && (
                            <span className="tw-ml-2 tw-inline-flex tw-items-center tw-rounded-full tw-bg-green-100 tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-green-800">
                                {savings_text}
                            </span>
                        )}
                    </button>
                </div>
            </div>

            {/* plan cards grid */}
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
                {display_plans.map((plan, index) => (
                    <Plan_card_component
                        key={plan.id}
                        plan={plan}
                        is_current={plan.id === current_plan_id}
                        is_popular={index === 1} // Growth is index 1
                        on_select={handle_select}
                        subscribing={subscribing}
                    />
                ))}
            </div>
        </div>
    )
}

/******************************************************************************
 ** Plan Comparison Inline Component ]
 ******************************************************************************/

/******************************************************************************
 ** New User Plan Selector Component [
 ******************************************************************************/

type new_user_plan_selector_props = {
    plans : P.usage_tier_t[]
    on_select : (plan : P.usage_tier_t) => Promise<void>
}

export const New_user_plan_selector = ({plans, on_select} : new_user_plan_selector_props) => {
    return (
        <div className="tw-mt-3">
            <C.Eres_card>
                <div className="tw-p-6">
                    <div className="tw-text-center tw-mb-6">
                        <h2 className="tw-text-2xl tw-font-bold tw-text-big-stone-900 tw-mb-2">
                            Welcome! Choose your plan to get started
                        </h2>
                        <p className="tw-text-gray-600">
                            Select the plan that fits your business needs
                        </p>
                    </div>

                    <Plan_comparison_inline
                        plans={plans}
                        on_select={on_select}
                    />

                    <div className="tw-mt-6 tw-text-center tw-text-sm tw-text-gray-600">
                        <p>
                            Need more than 15 brands?{' '}
                            <a href="/account" className="tw-text-big-stone-rlydark tw-underline">
                                Contact us
                            </a>{' '}
                            for Enterprise pricing
                        </p>
                    </div>
                </div>
            </C.Eres_card>
        </div>
    )
}

/******************************************************************************
 ** New User Plan Selector Component ]
 ******************************************************************************/
